import type { Unsubscribe } from 'firebase/firestore';
import {
  RecruiterApplicationSchema,
  RecruiterProfileSchema,
  type RecruiterApplication,
  type RecruiterProfile,
} from '@kilat/shared';
import { createRepo } from './_base.js';

export type { RecruiterApplication, RecruiterProfile } from '@kilat/shared';

/**
 * Sprint 82 — recruiter epic moderation surfaces for super-admin.
 *
 * `recruiters/{id}` is created by the `approveRecruiterApplication`
 * Cloud Function (which also mints the auth user + sets the custom
 * claim). Super-admin reads here for the "approved recruiters" list
 * on the moderation page.
 */
export const recruitersRepo = createRepo({
  path: 'recruiters',
  schema: RecruiterProfileSchema,
  name: 'recruiters',
});

/**
 * `recruiterApplications/{id}` — created only by the `submitRecruiterApplication`
 * callable (admin SDK); client create is denied, super-admin read/write
 * (infra/firestore.rules, security review 2026-07-17 R8). This repo does the
 * super-admin reads that drive the pending-queue list on `/superadmin/recruiters`.
 */
export const recruiterApplicationsRepo = createRepo({
  path: 'recruiterApplications',
  schema: RecruiterApplicationSchema,
  name: 'recruiterApplications',
});

/**
 * Brand-scoped live lists for `/superadmin/recruiters`. Both collections'
 * rules read `resource.data.platformBrandId`, and Firestore evaluates a LIST
 * query against the rule, so the unfiltered `listSnapshot()` is denied. The
 * equality filter on the caller's brand is what lets the query pass (ADR-0017).
 */
export function subscribeRecruiterApplicationsForBrand(
  brandId: string,
  cb: (apps: RecruiterApplication[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return recruiterApplicationsRepo.listSnapshotBounded(
    { where: [['platformBrandId', '==', brandId]] },
    cb,
    onError,
  );
}

export function subscribeRecruitersForBrand(
  brandId: string,
  cb: (recruiters: RecruiterProfile[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return recruitersRepo.listSnapshotBounded(
    { where: [['platformBrandId', '==', brandId]] },
    cb,
    onError,
  );
}
